<!-- Playlist editor page header + details card (Phase 3 editor template). -->

<template>
  <div class="ple-topbar">
    <header class="pe-header">
      <div class="pe-header__left">
        <div class="pe-header__title-row">
          <h1 class="bui-page-title">Playlist Editor</h1>
          <button
            type="button"
            class="pe-header__refresh"
            aria-label="Reload playlist"
            :disabled="isLoading"
            @click="reloadPlaylist()"
          >
            <PhArrowsClockwise :size="22" weight="bold" />
          </button>
        </div>
        <div class="pe-header__subtitle">{{ data.name }}</div>
      </div>
      <div class="pe-header__right">
        <CreateAnotherPlaylistButton />
      </div>
    </header>

    <InfoCard>
      <template #title>
        <h2 class="info-card__title-text">Playlist Details</h2>
      </template>

      <div class="ple-details-grid">
        <div class="d-flex field-row">
          <label for="ple-name" class="basis-1/2 text-sm font-bold">Name &#9733;</label>
          <TextField
            id="ple-name"
            v-model="data.name"
            class="basis-1/2"
            width="100%"
            :disabled="isReadOnly"
          />
        </div>
        <div class="d-flex field-row">
          <label for="ple-duration" class="basis-1/2 text-sm font-bold">Default Duration &#9733;</label>
          <NumberField
            id="ple-duration"
            v-model="data.defaultItemDuration"
            class="basis-1/2"
            width="100%"
            unit="Seconds"
            :disabled="isReadOnly"
          />
        </div>
        <div class="d-flex field-row">
          <label class="basis-1/2 text-sm font-bold">Sharing</label>
          <SelectField
            class="basis-1/2 ple-sharing"
            :options="sharingLevelOptions"
            v-model="data.sharingLevel"
            :disabled="isReadOnly"
          />
        </div>
        <div class="d-flex field-row">
          <label class="basis-1/2 text-sm font-bold">Campaigns</label>
          <div class="basis-1/2"><UsedBy :items="data.campaigns" label="Campaigns" /></div>
        </div>
        <div class="d-flex field-row">
          <label class="basis-1/2 text-sm font-bold">Size</label>
          <div class="basis-1/2"><Size :size="data.size" /></div>
        </div>
      </div>
    </InfoCard>
  </div>
</template>

<script lang="ts" setup>
import { PhArrowsClockwise } from '@phosphor-icons/vue';
import TextField from '@/components/form-fields/TextField.vue';
import NumberField from '@/components/form-fields/NumberField.vue';
import usePlaylist from '../composables/playlist';
import UsedBy from "@/components/ListingPages/ItemsLister/ItemsList/FieldsRenderers/UsedBy.vue"
import Size from "@/components/ListingPages/ItemsLister/ItemsList/FieldsRenderers/Size.vue"
import SelectField from '@/components/form-fields/SelectField/SelectField.vue';
import { sharingLevelOptions } from '@/data/options/sharingLevelOptions';
import CreateAnotherPlaylistButton from '../CreateAnotherPlaylistButton/CreateAnotherPlaylistButton.vue';
import InfoCard from '@/components/bui/molecules/InfoCard/InfoCard.vue';

defineProps<{
  isReadOnly: boolean
}>();

const {
  data,
  reloadPlaylist,
  isLoading
} = usePlaylist();

</script>

<style lang="scss" scoped>
@import '@/assets/styles/bui-editor-template';

.ple-topbar {
  display: flex;
  flex-direction: column;
  gap: var(--bui-page-section-gap, 24px);
}

/* "Create Another Playlist" as a tertiary pill on the page surface
   (same treatment as the Player Editor header buttons). */
.pe-header__right {
  :deep(.button) {
    background-color: var(--bui-ter-fill, rgba(246, 247, 255, 0.16));
    color: var(--bui-ter-text, #ffd9e6);
    border: 1px solid var(--bui-ter-border, rgba(246, 247, 255, 0.04));
    border-radius: var(--bui-ter-radius, 8px);
    text-transform: none;
    font-weight: 500;
    height: var(--bui-cta-h, 52px);
    box-shadow: none;
    padding: 0 var(--bui-ter-pad, 14px);

    &:hover {
      background-color: var(--bui-ter-fill-hover, rgba(246, 247, 255, 0.28));
    }
  }

  /* Dialog expander wrapper hugs the button. */
  :deep(> .flex) {
    display: inline-flex;
    width: auto;
    flex: 0 0 auto;
  }
}

/* Details laid out two rows per column on wide screens. */
.ple-details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.6rem 2rem;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }

  :deep(.field label) {
    color: var(--bui-tbl-row-text, #2a2a30);
  }

  :deep(.field input[type='text']),
  :deep(.field input[type='number']) {
    color: var(--bui-ter-input-text, #1a1a1e);
    background: var(--bui-ter-input-fill, rgba(0, 0, 0, 0.04));
    border-color: var(--bui-ter-input-border, rgba(0, 0, 0, 0.12));
    width: 100%;
  }

  /* Legacy SelectField: token colours for the styled-select shell. */
  :deep(.styled-select) {
    border-color: var(--bui-ter-input-border, rgba(0, 0, 0, 0.12));
    border-radius: var(--bui-ter-radius, 8px);
    background: var(--bui-ter-input-fill, rgba(0, 0, 0, 0.04));

    select {
      color: var(--bui-ter-input-text, #1a1a1e);
    }
  }

  /* NumberField's unit suffix. */
  :deep(.unit) {
    color: var(--bui-tbl-detail-text, #5c5c66);
  }
}
</style>
